import decimalsMask from './DecimalsMask';

/**
 * Gets the decimal precision (decimal places) for a field.
 *
 * Priority order:
 * 1. Field-specific decimal precision from mappingConfig.columns[fieldName].decimalPrecision
 * 2. Unit-based precision from decimalsMask[unit]
 * 3. Fallback precision (default: 2)
 *
 * @param {string} fieldName - The field name (e.g., 'liquidVolume', 'tav')
 * @param {string} unit - The unit name (e.g., 'L', 'cm', 'celsius')
 * @param {Object} mappingConfig - The mapping configuration object (optional)
 * @param {number} fallbackPrecision - Fallback precision if nothing is found (default: 2)
 * @returns {number} The decimal precision to use
 */
export function getDecimalPrecision(fieldName, unit, mappingConfig = null, fallbackPrecision = 2) {
  // Validate fallback precision is a valid number
  const safeFallback = typeof fallbackPrecision === 'number' && fallbackPrecision >= 0
    ? fallbackPrecision
    : 2;

  // Check if field-specific decimal precision is configured in mappingConfig
  if (mappingConfig?.columns?.[fieldName]?.decimalPrecision !== undefined) {
    const customPrecision = mappingConfig.columns[fieldName].decimalPrecision;
    // Validate custom precision is a valid number
    if (typeof customPrecision === 'number' && customPrecision >= 0) {
      return customPrecision;
    }
  }

  // Fall back to unit-based precision from the mask
  if (unit && typeof unit === 'string' && decimalsMask[unit] !== undefined) {
    const maskPrecision = decimalsMask[unit];
    // Validate mask precision is a valid number
    if (typeof maskPrecision === 'number' && maskPrecision >= 0) {
      return maskPrecision;
    }
  }

  // Final fallback
  return safeFallback;
}

export default getDecimalPrecision;
